import { type FC, useEffect, useRef, useState } from 'react';
import { cn } from '@components/lib/utils';
import type { EvalRunSummary } from '@queries/evalRuns';
import { ChevronDown } from 'lucide-react';

import { RUN_STATUS_LABEL } from '../constants';

import { RunStatusChip } from './StatusChip';

interface RunSwitcherProps {
  runs: EvalRunSummary[];
  activeRunId?: string;
  onSelect: (runId: string) => void;
}

const formatRunLabel = (run: EvalRunSummary) => {
  const date = run.created_at ? new Date(run.created_at) : null;
  const ts = date && !Number.isNaN(date.valueOf()) ? date.toLocaleString() : 'Unknown time';
  const configName = run.eval_config_name ?? run.eval_config_id.slice(0, 8);
  const version = run.eval_config_version_number ? ` v${run.eval_config_version_number}` : '';
  return `${configName}${version} · ${ts}`;
};

/**
 * Internal-only: a compact dropdown listing every run for the submission,
 * most recent first (already sorted by the handler). The active run is the
 * button label; each option carries a status chip so staff can tell which
 * runs completed vs failed.
 */
export const RunSwitcher: FC<RunSwitcherProps> = ({ runs, activeRunId, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handler);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const activeRun = runs.find((r) => r.id === activeRunId);

  return (
    <div className="relative w-full" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        disabled={runs.length === 0}
        data-testid="pbqa-run-switcher"
        className={cn(
          'flex h-8 w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-2.5 text-left text-xs',
          'focus:border-primary-500 focus:ring-primary-500 focus:ring-1 focus:outline-none',
          runs.length === 0 ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50',
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="min-w-0 truncate text-gray-700">
          {activeRun ? formatRunLabel(activeRun) : runs.length === 0 ? 'No runs yet' : 'Select a run'}
        </span>
        {activeRun && (
          <span className="shrink-0 text-[11px] text-gray-500">
            {activeRun.findings_count} {activeRun.findings_count === 1 ? 'finding' : 'findings'} ·{' '}
            {RUN_STATUS_LABEL[activeRun.status]}
          </span>
        )}
        <ChevronDown className={cn('size-4 shrink-0 text-gray-500 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && runs.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 max-h-80 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {runs.map((run) => {
            const isSelected = run.id === activeRunId;
            return (
              <li key={run.id} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  data-testid={`pbqa-run-option-${run.id}`}
                  onClick={() => {
                    onSelect(run.id);
                    setIsOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs',
                    'hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
                    isSelected && 'bg-gray-50',
                  )}
                >
                  <span className="truncate">{formatRunLabel(run)}</span>
                  <RunStatusChip status={run.status} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
